import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { SelectNativo } from '@/components/ui/select-nativo';
import { Aviso } from '@/components/ui/aviso';
import { useToast } from '@/lib/hooks/use-toast';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { adminService } from '../services/admin-service';
import type { OpcionesDeLaDemo } from '../types/admin.types';
import { TEXTOS_DEL_CAMBIO } from '../lib/demo-textos';

interface Props {
  abierto: boolean;
  empresaId: string;
  nombre: string;
  /** Lo que el servidor ofrece hoy (rubros y tiendas elegibles) y lo que la empresa tiene. */
  opciones: OpcionesDeLaDemo;
  onCerrar: () => void;
  /** Se cambió algo (o las opciones quedaron viejas): que la ficha vuelva a leerlas. */
  onCambiado: () => void;
}

/** La tienda que se ofrece al elegir un rubro: la sugerida de ese rubro o, si no hay, la primera. */
function tiendaPorOmision(opciones: OpcionesDeLaDemo, ejeId: string): string {
  const delEje = opciones.tiendas.filter((t) => t.ejeId === ejeId);
  return (delEje.find((t) => t.sugerida) ?? delEje[0] ?? opciones.tiendas[0])?.sitio ?? '';
}

/**
 * «Cambiar rubro y tienda» (343, A1): el administrador de plataforma elige el rubro nuevo de una
 * empresa en prueba y, si quiere, su tienda. Con tienda es EL MISMO cambio que hace el comercio
 * (guarda la anterior con sus ejemplos y carga los de la nueva); no hay lógica aparte en la pantalla.
 * Lo que se puede elegir lo manda el servidor (los mismos rubros y tiendas que ofrece la invitación).
 */
export function CambiarRubroYTiendaDialog({
  abierto,
  empresaId,
  nombre,
  opciones,
  onCerrar,
  onCambiado,
}: Props) {
  const { toast } = useToast();
  const actual = opciones.actual;
  // Dejar la tienda que tiene sólo se ofrece si todavía se puede elegir (si no, hay que pasar a otra).
  const puedeDejarla = !!actual && opciones.tiendas.some((t) => t.sitio === actual.sitio);

  const ejeInicial = opciones.ejes.some((e) => e.id === opciones.demo.eje?.id)
    ? (opciones.demo.eje?.id ?? '')
    : (opciones.ejes[0]?.id ?? '');
  const [ejeId, setEjeId] = useState(ejeInicial);
  const [sitio, setSitio] = useState(puedeDejarla ? '' : tiendaPorOmision(opciones, ejeInicial));
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sólo AL ABRIR se parte de cero. Si las opciones se vuelven a leer con el diálogo abierto (tras un 422 o un
  // 404 la ficha las relee), el mensaje del servidor y lo que la persona eligió se conservan.
  const estabaAbierto = useRef(false);
  useEffect(() => {
    if (abierto && !estabaAbierto.current) {
      setEjeId(ejeInicial);
      setSitio(puedeDejarla ? '' : tiendaPorOmision(opciones, ejeInicial));
      setError(null);
    }
    estabaAbierto.current = abierto;
  }, [abierto, opciones, ejeInicial, puedeDejarla]);

  // Con opciones nuevas, la selección se valida: un rubro o una tienda que ya no se ofrece se cambia por una
  // que sí; lo demás queda como estaba.
  useEffect(() => {
    if (!abierto) return;
    const ejeVigente = opciones.ejes.some((e) => e.id === ejeId) ? ejeId : ejeInicial;
    if (ejeVigente !== ejeId) setEjeId(ejeVigente);
    const sitioVigente =
      sitio === '' ? puedeDejarla : opciones.tiendas.some((t) => t.sitio === sitio);
    if (!sitioVigente) setSitio(puedeDejarla ? '' : tiendaPorOmision(opciones, ejeVigente));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opciones]);

  const delRubro = useMemo(
    () =>
      opciones.tiendas
        .filter((t) => t.ejeId === ejeId && !(puedeDejarla && t.sitio === actual?.sitio))
        .sort((a, b) => Number(b.sugerida) - Number(a.sugerida)),
    [opciones.tiendas, ejeId, puedeDejarla, actual?.sitio]
  );
  const otras = useMemo(
    () =>
      opciones.tiendas.filter(
        (t) => t.ejeId !== ejeId && !(puedeDejarla && t.sitio === actual?.sitio)
      ),
    [opciones.tiendas, ejeId, puedeDejarla, actual?.sitio]
  );

  const sitioElegido = sitio === actual?.sitio ? '' : sitio;
  const cambiaElRubro = ejeId !== (opciones.demo.eje?.id ?? '');
  const cambiaLaTienda = sitioElegido !== '';
  const hayCambio = ejeId !== '' && (cambiaElRubro || cambiaLaTienda);

  const cambiarRubro = (nuevo: string) => {
    setEjeId(nuevo);
    // Sin la actual a mano, al cambiar de rubro se propone la de ese rubro.
    if (!puedeDejarla) setSitio(tiendaPorOmision(opciones, nuevo));
  };

  const cambiar = async () => {
    setTrabajando(true);
    setError(null);
    try {
      const r = await adminService.cambiarEjeDeLaDemo(empresaId, {
        ejeId,
        ...(sitioElegido ? { sitio: sitioElegido } : {}),
      });
      toast({
        title: r.sinCambios
          ? TEXTOS_DEL_CAMBIO.sinCambios(nombre)
          : TEXTOS_DEL_CAMBIO.hecho(nombre, r.eje.nombre, r.tienda.nombre),
        description:
          [
            TEXTOS_DEL_CAMBIO.ejemplosCargados(
              r.ejemplos.archivados,
              r.ejemplos.sembrados,
              r.ejemplos.desarchivados
            ),
            r.siembraPendiente ? TEXTOS_DEL_CAMBIO.siembraPendiente : undefined,
          ]
            .filter(Boolean)
            .join(' ') || undefined,
      });
      onCambiado();
      onCerrar();
    } catch (err) {
      setError(mensajeDeError(err, TEXTOS_DEL_CAMBIO.noSePudo));
      // Ante cualquier falla se vuelve a leer: un rubro o una tienda pudo apagarse (404, 409, 422) o el cambio
      // pudo quedar hecho antes de fallar (500). El mensaje y la selección se conservan.
      onCambiado();
    } finally {
      setTrabajando(false);
    }
  };

  return (
    <Dialog open={abierto} onOpenChange={(v) => !v && !trabajando && onCerrar()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{TEXTOS_DEL_CAMBIO.titulo}</DialogTitle>
          <DialogDescription>{TEXTOS_DEL_CAMBIO.ayuda(nombre)}</DialogDescription>
        </DialogHeader>

        {opciones.ejes.length === 0 ? (
          <Aviso tono="alerta">{TEXTOS_DEL_CAMBIO.sinOpciones}</Aviso>
        ) : (
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="cambiar-eje-rubro">{TEXTOS_DEL_CAMBIO.rubro}</Label>
              <SelectNativo
                id="cambiar-eje-rubro"
                className="w-full"
                value={ejeId}
                onChange={(e) => cambiarRubro(e.target.value)}
                disabled={trabajando}
              >
                {opciones.ejes.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.nombre}
                  </option>
                ))}
              </SelectNativo>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cambiar-eje-tienda">{TEXTOS_DEL_CAMBIO.tienda}</Label>
              <SelectNativo
                id="cambiar-eje-tienda"
                className="w-full"
                value={sitio}
                onChange={(e) => setSitio(e.target.value)}
                disabled={trabajando}
              >
                {puedeDejarla && actual && (
                  <option value="">{TEXTOS_DEL_CAMBIO.dejarLaActual(actual.nombre)}</option>
                )}
                {delRubro.length > 0 && (
                  <optgroup label={TEXTOS_DEL_CAMBIO.deEsteRubro}>
                    {delRubro.map((t) => (
                      <option key={t.sitio} value={t.sitio}>
                        {t.nombre}
                        {t.sugerida ? ` · ${TEXTOS_DEL_CAMBIO.sugerida}` : ''}
                        {` · ${TEXTOS_DEL_CAMBIO.ejemplos(t.ejemplos)}`}
                      </option>
                    ))}
                  </optgroup>
                )}
                {otras.length > 0 && (
                  <optgroup label={TEXTOS_DEL_CAMBIO.otras}>
                    {otras.map((t) => (
                      <option key={t.sitio} value={t.sitio}>
                        {`${t.nombre} · ${TEXTOS_DEL_CAMBIO.ejemplos(t.ejemplos)}`}
                      </option>
                    ))}
                  </optgroup>
                )}
              </SelectNativo>
            </div>

            {opciones.demo.ejeEstado === 'SIN_TIENDAS_LEIDAS' && opciones.demo.eje && (
              <Aviso tono="alerta">
                {TEXTOS_DEL_CAMBIO.rubroSinTiendas(opciones.demo.eje.nombre)}
              </Aviso>
            )}

            {error && (
              <Aviso tono="bloqueo" role="alert">
                {error}
              </Aviso>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onCerrar} disabled={trabajando}>
            {TEXTOS_DEL_CAMBIO.cancelar}
          </Button>
          <Button onClick={cambiar} disabled={!hayCambio || trabajando}>
            {trabajando && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
            {trabajando ? TEXTOS_DEL_CAMBIO.trabajando : TEXTOS_DEL_CAMBIO.cambiar}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
